import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileIcon = (filename = '') => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <PictureAsPdfIcon sx={{ color: '#ef4444', fontSize: 'clamp(18px, 1.4vw, 22px)' }} />;
  if (['jpg', 'jpeg', 'png'].includes(ext)) return <ImageIcon sx={{ color: '#3b82f6', fontSize: 'clamp(18px, 1.4vw, 22px)' }} />;
  return <InsertDriveFileIcon sx={{ color: '#64748b', fontSize: 'clamp(18px, 1.4vw, 22px)' }} />;
};

const FileUploadModal = ({ open, onClose, onFilesSelected, onSave }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setSelectedFiles((prevFiles) => {
      const allFiles = [...prevFiles, ...files];
      const uniqueFiles = Array.from(
        new Map(
          allFiles.map((file) => [
            `${file.name}-${file.size}-${file.lastModified}`,
            file,
          ])
        ).values()
      );

      onFilesSelected?.(uniqueFiles);
      return uniqueFiles;
    });
  }, [onFilesSelected]);

  const handleFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    onFilesSelected?.(updatedFiles);
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setIsDragging(false);
    onFilesSelected?.([]);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setSelectedFiles([]);
      setIsDragging(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.25)',
          background: '#ffffff',
        },
      }}
    >
      {/* Header institucional moderno */}
      <DialogTitle
        sx={{
          m: 0,
          px: 'clamp(12px, 1.5vw, 18px)',
          py: 'clamp(10px, 1.2vw, 14px)',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(8px, 1vw, 12px)',
          background: 'linear-gradient(135deg, #0655A5 0%, #033C75 100%)',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(6, 85, 165, 0.25)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.18)',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)', color: '#ffffff' }} />
        </Paper>
        <Typography sx={{ fontWeight: 800, flex: 1, fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1rem)', lineHeight: 1.2 }}>
          Subir Archivos y Comprobantes
        </Typography>
        {selectedFiles.length > 0 && (
          <Chip
            size="small"
            label={`${selectedFiles.length} ${selectedFiles.length === 1 ? 'archivo' : 'archivos'}`}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: 'clamp(0.65rem, 0.60rem + 0.08vw, 0.72rem)',
              height: 'clamp(20px, 1.6vw, 24px)',
              mr: 1,
            }}
          />
        )}
        <Tooltip title="Cerrar">
          <IconButton onClick={handleClose} sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            <CloseIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)' }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      {/* Contenido principal */}
      <DialogContent sx={{ p: 'clamp(14px, 2vw, 20px)', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Dropzone estilizado */}
        <Box
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: isDragging ? '#0655A5' : '#cbd5e1',
            borderRadius: '12px',
            p: 'clamp(14px, 2vw, 24px)',
            textAlign: 'center',
            backgroundColor: isDragging ? 'rgba(6, 85, 165, 0.05)' : '#f8fafc',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#0655A5',
              backgroundColor: 'rgba(6, 85, 165, 0.02)',
            },
          }}
        >
          <input
            type="file"
            id="adjunto"
            accept={ACCEPTED_FILE_TYPES}
            style={{ display: 'none' }}
            multiple
            onChange={handleFileChange}
          />
          <label htmlFor="adjunto" style={{ cursor: 'pointer', display: 'block' }}>
            <Box
              sx={{
                width: 'clamp(36px, 3.5vw, 44px)',
                height: 'clamp(36px, 3.5vw, 44px)',
                borderRadius: '50%',
                bgcolor: isDragging ? '#0655A5' : 'rgba(6, 85, 165, 0.08)',
                color: isDragging ? '#ffffff' : '#0655A5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1,
                transition: 'all 0.2s ease',
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 'clamp(20px, 2vw, 26px)' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 'clamp(0.76rem, 0.72rem + 0.1vw, 0.85rem)', color: '#0655A5', mb: 0.3 }}>
              Haz clic para seleccionar archivos
            </Typography>
            <Typography sx={{ fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.74rem)', color: '#64748b' }}>
              o arrastra y suelta aquí (PDF, JPG, PNG)
            </Typography>
          </label>
        </Box>

        {/* Lista de archivos seleccionados */}
        {selectedFiles.length > 0 && (
          <Box sx={{ maxHeight: 'clamp(140px, 22vh, 220px)', overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.78rem)', color: '#334155', px: 0.5 }}>
              Archivos preparados para subir ({selectedFiles.length}):
            </Typography>
            {selectedFiles.map((file, idx) => (
              <Paper
                key={`${file.name}-${file.size}-${file.lastModified}`}
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 'clamp(6px, 0.8vw, 10px) clamp(8px, 1vw, 12px)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: '#cbd5e1' },
                }}
              >
                {getFileIcon(file.name)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    title={file.name}
                    sx={{ fontWeight: 600, fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.78rem)', color: '#1e293b' }}
                  >
                    {file.name}
                  </Typography>
                  <Typography sx={{ fontSize: 'clamp(0.60rem, 0.56rem + 0.06vw, 0.68rem)', color: '#94a3b8' }}>
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <Tooltip title="Eliminar de la lista">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(idx)}
                    sx={{ color: '#94a3b8', p: 0.5, '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.08)' } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 18px)' }} />
                  </IconButton>
                </Tooltip>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* Botones inferiores */}
      <DialogActions sx={{ px: 'clamp(12px, 1.5vw, 18px)', py: 'clamp(8px, 1.2vw, 12px)', bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 0.8 }}>
        <Button
          onClick={handleClose}
          size="small"
          sx={{
            borderRadius: '8px',
            color: '#64748b',
            fontWeight: 600,
            fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.76rem)',
            textTransform: 'none',
            px: 'clamp(10px, 1.2vw, 14px)',
            height: 'clamp(26px, 2vw, 30px)',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={selectedFiles.length === 0}
          onClick={() => onSave?.(selectedFiles)}
          sx={{
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.76rem)',
            bgcolor: '#0655A5',
            boxShadow: 'none',
            textTransform: 'none',
            px: 'clamp(14px, 1.6vw, 20px)',
            height: 'clamp(26px, 2vw, 30px)',
            '&:hover': { bgcolor: '#043d77', boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' },
          }}
        >
          Guardar Archivos
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadModal;
